import classNames from 'classnames';
import React from 'react';
import ExpandableDropdown from '../components/ExpandableDropdown';
import SVGIcon from '../components/SVGIcon';
import SelectEntityOption from '../components/SelectEntityOption';
import Timestamp from '../components/Timestamp';
import { PostType } from '../graphql/generated/graphql';
import GroupIcon from '../groups/GroupIcon';
import cloudSVG from '../icons/non-prefilled/tabbar/cloud.svg';
import homeSVG from '../icons/prefilled/16x16/home.svg';
import { PostIconForState } from '../post/PostIcon';
import { TopicEntityIcon } from '../topic/TopicIcon';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import * as styles from './TabBarMenu.module.scss';
import type { TabBarMenuItem, TabIcon } from './shared-types';

const iconTypes: Record<TabIcon, React.ReactNode> = {
  cloud: (
    <SVGIcon className={classNames(styles.entityIcon, styles.cloud)}>
      {cloudSVG}
    </SVGIcon>
  ),
  home: (
    <SVGIcon className={classNames(styles.entityIcon, styles.home)}>
      {homeSVG}
    </SVGIcon>
  ),
  post: (
    <PostIconForState
      state={{
        type: PostType.Post,
        draft: false,
        archived: false,
        public: false,
      }}
      prefilled
    />
  ),
  topic: <TopicEntityIcon />,
  template: (
    <PostIconForState
      state={{
        type: PostType.Template,
        draft: false,
        archived: false,
        public: false,
      }}
      prefilled
    />
  ),
  drafts: (
    <PostIconForState
      state={{
        type: PostType.Post,
        draft: true,
        archived: false,
        public: false,
      }}
      prefilled
    />
  ),

  group: <GroupIcon />,
};

interface TabBarMenuProps {
  openTabs: TabBarMenuItem[];
  recentTabs: TabBarMenuItem[];
  onSelect: (tab: TabBarMenuItem) => void;
  onClose: () => void;
}

const TabBarMenu = ({
  openTabs,
  recentTabs,
  onSelect,
  onClose,
}: TabBarMenuProps) => {
  const getIcon = (tab: TabBarMenuItem) =>
    tab.iconUrl ? (
      <img src={tab.iconUrl} className={styles.avatarIcon} alt="" />
    ) : (
      iconTypes[tab.icon] || null
    );

  return (
    <ClickOutsideHandler onClickOutside={onClose}>
      <ExpandableDropdown.Root
        onSelect={(item) => {
          onSelect(item.data.tab);
        }}
        sections={[
          {
            label: 'Open tabs',
            items: openTabs.map((tab) => ({
              id: tab.id.toString(),
              data: {
                label: tab.title,
                icon: getIcon(tab),
                secondary: tab.isCurrent ? (
                  'Currently open'
                ) : (
                  <>
                    Viewed <Timestamp date={tab.lastActivityTime} />
                  </>
                ),
                tab,
              },
            })),
            isExpandable: true,
            preferredNumberOfItems: 6,
          },
          {
            label: 'Recently closed',
            items: recentTabs.map((tab) => ({
              id: tab.id.toString(),
              data: {
                label: tab.title,
                icon: getIcon(tab),
                secondary: <Timestamp date={tab.lastActivityTime} />,
                tab,
              },
            })),
            isExpandable: true,
            preferredNumberOfItems: 3,
          },
        ]}
        maxItemsToShow={9}
        renderItem={(item) => <SelectEntityOption {...item.data} />}
      >
        <div className={styles.container}>
          <ExpandableDropdown.Menu />
        </div>
      </ExpandableDropdown.Root>
    </ClickOutsideHandler>
  );
};
export default TabBarMenu;
